import React, { useState, useEffect } from 'react';
import { userAPI } from '../utils/api';
import { Link } from 'react-router-dom';
import DropZone from '../components/DropZone';
import ProgressBar from '../components/ProgressBar';
import ShareLink from '../components/ShareLink';
import AdUnit from '../components/AdUnit';
import { uploadAPI, utils } from '../utils/api';
import config, { logger } from '../utils/config';

function HomePage() {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [currentFileName, setCurrentFileName] = useState('');
  const [currentFolderId, setCurrentFolderId] = useState(null);
  
  // Enhanced upload tracking
  const [files, setFiles] = useState([]);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const lastBytesByFileRef = React.useRef(new Map());
  const totalBytesRef = React.useRef(0);
  const startTimeRef = React.useRef(0);
  const [bytesUploaded, setBytesUploaded] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [cancelUpload, setCancelUpload] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0); // Processing progress
  const [processedFiles, setProcessedFiles] = useState(0); // Files processed count

  // Removed HomePage expiry modal; DropZone handles expiry selection

  // Ensure homepage tab title is correct
  useEffect(() => {
    document.title = 'Send large files - LargeTransfer';
  }, []);

  const handleFileUpload = async (files, expiryDays = 5) => {
    const fileArray = Array.isArray(files) ? files : [files];
    
    // Initialize upload state
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    setUploadResult(null);
    setCurrentFileIndex(0);
    setTotalFiles(fileArray.length);
    setFiles(fileArray);
    
    // Calculate total bytes
    const totalFileSize = fileArray.reduce((sum, file) => sum + file.size, 0);
    setTotalBytes(totalFileSize);
    setBytesUploaded(0);
    setUploadSpeed(0);
    lastBytesByFileRef.current = new Map();
    setStartTime(Date.now());
    
    // Setup cancellation
    let uploadCancelled = false;
    setCancelUpload(() => () => {
      uploadCancelled = true;
      setError('Upload cancelled by user');
      setUploading(false);
    });

    try {
      // Step 1: Initialize upload and get signed URLs with consistent user ID
      console.log('Initializing upload for', fileArray.length, 'files');
      const grandTotalSize = fileArray.reduce((sum, f) => sum + (f?.size || 0), 0);
      setTotalBytes(grandTotalSize);
      totalBytesRef.current = grandTotalSize;
      const startedAt = Date.now();
      setStartTime(startedAt);
      startTimeRef.current = startedAt;
      const currentUserId = utils.getCurrentUserId();
      console.log('Using consistent user ID for upload:', currentUserId);
      
      const initResult = await uploadAPI.initializeUpload(fileArray, { 
        userId: currentUserId,
        expiryDays: expiryDays
      });
      
      console.log('Upload initialized:', initResult);
      setCurrentFolderId(initResult.folderId);
      
      // INSTANT SHARE LINK: Show immediately after R2 upload (like SendGB)
      console.log('🚀 INSTANT: All files uploaded to R2 - showing share link immediately');
      
      // Set progress to show upload is starting
      setUploadProgress(5);
      
      // Step 2: Upload files in controlled batches to avoid browser limits
      const BATCH_SIZE = 6; // Browser connection limit (6-8 concurrent connections)
      let completedFiles = 0;
      let totalBytesUploaded = 0;
      const totalProgressPerFile = 100 / fileArray.length;
      
      console.log(`🚀 ENTERPRISE: Uploading ${fileArray.length} files in batches of ${BATCH_SIZE}`);
      
      const MAX_RETRIES = 3;
      const baseDelayMs = 800;
      const sleep = (ms) => new Promise(r => setTimeout(r, ms));
      const backoffDelay = (attempt) => baseDelayMs * Math.pow(2, attempt);
      
      for (let batchStart = 0; batchStart < fileArray.length; batchStart += BATCH_SIZE) {
        const batchEnd = Math.min(batchStart + BATCH_SIZE, fileArray.length);
        
        console.log(`📦 Processing batch ${Math.floor(batchStart / BATCH_SIZE) + 1}: files ${batchStart + 1}-${batchEnd}`);
        
        // Upload this batch in parallel
        const batchPromises = [];
        
        for (let i = batchStart; i < batchEnd; i++) {
        // Check for cancellation
        if (uploadCancelled) {
          throw new Error('Upload cancelled');
        }
        
        const file = fileArray[i];
        const uploadInfo = initResult.uploadUrls[i];
        
        setCurrentFileIndex(i);
        setCurrentFileName(file.name);
        
          console.log(`📤 Uploading file ${i + 1}/${fileArray.length}: ${file.name}`);
          
          // Check if upload is required (R2 configured)
          if (uploadInfo.requiresUpload && uploadInfo.uploadUrl !== 'local-mock') {
            // Capture current values for closure
            const currentCompletedFiles = completedFiles;
            
            const uploadWithRetry = async () => {
              for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
                try {
            await uploadAPI.uploadToR2Smart(
              file, 
              uploadInfo.uploadUrl, 
              uploadInfo.fileId, 
              uploadInfo.r2Key, 
              (progress) => {
                      // Compute delta bytes for this file to avoid double counting
                      const currentBytes = Math.max(0, Math.min(file.size, (progress / 100) * file.size));
                      const lastBytes = lastBytesByFileRef.current.get(uploadInfo.fileId) || 0;
                      const delta = Math.max(0, currentBytes - lastBytes);
                      if (delta > 0) {
                        lastBytesByFileRef.current.set(uploadInfo.fileId, currentBytes);
                        const elapsed = Math.max(0.001, (Date.now() - startTimeRef.current) / 1000);
                        setBytesUploaded(prev => {
                          const newTotal = Math.min(prev + delta, totalBytesRef.current || grandTotalSize);
                          setUploadSpeed(newTotal / elapsed);
                          return newTotal;
                        });
                      }
                const fileProgress = (currentCompletedFiles * totalProgressPerFile) + (progress * totalProgressPerFile / 100);
                      setUploadProgress(Math.max(0, Math.min(99, fileProgress)));
                    }
                  );
                  return; // success
                } catch (err) {
                  const willRetry = attempt < MAX_RETRIES;
                  console.warn(`🔁 Upload failed (attempt ${attempt + 1}/${MAX_RETRIES + 1}) for ${file.name}: ${err?.message}`);
                  if (!willRetry) throw err;
                  const delay = backoffDelay(attempt);
                  console.log(`⏳ Backing off ${delay}ms before retrying ${file.name}...`);
                  await sleep(delay);
                }
              }
            };
            
            const uploadPromise = uploadWithRetry()
              .then(() => {
                completedFiles++;
            totalBytesUploaded += file.size;
                console.log(`✅ File ${i + 1} uploaded successfully: ${file.name}`);
                
                // Start background processing (fire-and-forget)
                uploadAPI.completeUploadAsync(uploadInfo.fileId, true, {
            folderId: initResult.folderId,
            userId: currentUserId,
            r2Key: uploadInfo.r2Key,
            filename: file.name,
            fileSize: file.size,
            contentType: file.type,
            expiryDate: initResult.expiryDate
                }).catch(error => {
                  console.warn(`⚠️ Background processing failed for file ${i + 1}:`, error);
                });
              })
              .catch(error => {
                console.error(`❌ File ${i + 1} upload failed: ${file.name}`, error);
                throw error;
              });
            
            batchPromises.push(uploadPromise);
            
          } else {
            console.log('Skipping file upload (local development mode)');
            // Simulate upload for local development
            const simulatePromise = new Promise((resolve) => {
              setTimeout(() => {
          completedFiles++;
                totalBytesUploaded += file.size;
                console.log(`✅ File ${i + 1} simulated successfully: ${file.name}`);
                resolve();
              }, 100);
            });
            batchPromises.push(simulatePromise);
          }
        }
        
        // Wait for this batch to complete
        const batchResults = await Promise.all(batchPromises);
        
        // Track successful uploads percentage for UX (optional)
        const batchSuccess = batchResults.filter(r => r === undefined || r?.success).length;
        console.log(`📊 Batch completed: ${batchSuccess}/${batchResults.length} files succeeded`);
      }
      
      // ENTERPRISE: Only show success if ALL files uploaded successfully
      if (completedFiles !== fileArray.length) {
        const failedCount = fileArray.length - completedFiles;
        console.error(`❌ ENTERPRISE ERROR: ${failedCount} files failed to upload out of ${fileArray.length}`);
        
        // Show error to user
        setError(`${failedCount} out of ${fileArray.length} files failed to upload. Please try again.`);
        setUploading(false);
        return;
      }
      
      // Only proceed if ALL files uploaded successfully
      console.log('✅ ENTERPRISE: All files uploaded successfully to R2');
      
      // INSTANT SHARE LINK: Show immediately after R2 upload (like SendGB)
      setUploadProgress(100);
      setProcessingProgress(100); // Processing complete
      
      // Calculate total size for share link
      const totalSize = fileArray.reduce((sum, file) => sum + file.size, 0);
      
      // Show share link IMMEDIATELY after upload to R2 (no verification wait)
      setUploadResult({
        folderId: initResult.folderId,
        shareLink: initResult.shareLink,
        filename: fileArray.length === 1 ? fileArray[0].name : `${fileArray.length} files`,
        fileCount: fileArray.length,
        totalSize: totalSize,
        uploadDate: new Date().toISOString(),
        status: 'completed' // All files uploaded to R2
      });
      
      console.log('✅ INSTANT: Share link shown immediately after R2 upload - processing continues in background');
      // Try to refresh My Files if user is on uploads page later
      try {
        // best-effort ping to avoid stale caches
        await userAPI.getUserFolders();
      } catch (_) {}
      
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Home delegates expiry to DropZone via its dropdown; pass directly to uploader
  const handleSelectDirect = (files, expiryDays) => {
    handleFileUpload(files, expiryDays ?? 5);
  };

  const resetUpload = () => {
    setUploadResult(null);
    setError(null);
    setUploadProgress(0);
    setCurrentFileIndex(0);
    setCurrentFileName('');
    setCurrentFolderId(null);
    
    // Reset enhanced tracking
    setFiles([]);
    setUploadSpeed(0);
    setBytesUploaded(0);
    setTotalBytes(0);
    setStartTime(0);
    setCancelUpload(null);
  };

  const FileIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M9.5 1H3a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V4.5L9.5 1ZM3 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4.293a1 1 0 0 0-.293-.707l-4-4A1 1 0 0 0 10 3H3Z"/>
    </svg>
  );

  const ShieldIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 0c-.69 0-1.843.265-2.928.56-1.11.3-2.229.655-2.887.87a1.54 1.54 0 0 0-1.044 1.262c-.596 4.477.787 7.795 2.465 9.99a11.777 11.777 0 0 0 2.517 2.453c.386.273.744.482 1.048.625.28.132.581.24.829.24s.548-.108.829-.24c.304-.143.662-.352 1.048-.625a11.777 11.777 0 0 0 2.517-2.453c1.678-2.195 3.061-5.513 2.465-9.99a1.541 1.541 0 0 0-1.044-1.263 62.467 62.467 0 0 0-2.887-.87C9.843.266 8.69 0 8 0Z"/>
    </svg>
  );

  const LightningIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M11.251.068a.5.5 0 0 1 .227.58L9.677 6.5H13a.5.5 0 0 1 .364.843l-8 8.5a.5.5 0 0 1-.842-.49L6.323 9.5H3a.5.5 0 0 1-.364-.843l8-8.5a.5.5 0 0 1 .615-.09Z"/>
    </svg>
  );

  const ClockIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
      <path d="M7.5 3a.5.5 0 0 1 .5.5v5.21l3.248 1.856a.5.5 0 0 1-.496.868l-3.5-2A.5.5 0 0 1 7 9V3.5a.5.5 0 0 1 .5-.5z"/>
    </svg>
  );

  const LockIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
    </svg>
  );

  return (
    <div style={{ backgroundColor: '#1a1d29', color: '#e2e8f0', minHeight: '100vh' }}>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-6">
          {/* Brand Logo/Text */}
          <div style={{ marginBottom: '12px' }}>
            <div className="hidden md:flex" style={{ 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '6px',
              marginBottom: '8px'
            }}>
              <div style={{ color: '#fbbf24' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0,0,256,256">
                  <g fill="#d1d5db" fillRule="nonzero" stroke="none" strokeWidth="1" strokeLinecap="butt" strokeLinejoin="miter" strokeMiterlimit="10" strokeDasharray="" strokeDashoffset="0" fontFamily="none" fontWeight="none" fontSize="none" textAnchor="none" style={{mixBlendMode: 'normal'}}>
                    <g transform="scale(10.66667,10.66667)">
                      <path d="M5.75,3c-0.35548,0.00019 -0.68414,0.18906 -0.86328,0.49609l-1.75,3c-0.08939,0.1529 -0.13657,0.32679 -0.13672,0.50391v12c0,1.09306 0.90694,2 2,2h14c1.09306,0 2,-0.90694 2,-2v-12c-0.00015,-0.17711 -0.04733,-0.35101 -0.13672,-0.50391l-1.75,-3c-0.17914,-0.30704 -0.5078,-0.4959 -0.86328,-0.49609zM6.32422,5h11.35156l1.16602,2h-13.68359zM10,9h4c0.552,0 1,0.448 1,1c0,0.552 -0.448,1 -1,1h-4c-0.552,0 -1,-0.448 -1,-1c0,-0.552 0.448,-1 1,-1z"></path>
                    </g>
                  </g>
                </svg>
              </div>
              <h1 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#e2e8f0', 
                margin: '0',
                letterSpacing: '-0.02em'
              }}>
                LargeTransfer
              </h1>
            </div>
            <div className="hidden md:flex" style={{ 
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              marginBottom: '8px'
            }}>
              <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '500' }}>
                Send Large Files Free
              </span>
              <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '500' }}>
                Unlimited File Size • 100GB Free Bandwidth • Premium Available
              </span>
            </div>
          </div>
          
          {/* Mobile Ad Unit */}
          <div className="block md:hidden max-w-sm mx-auto mb-4">
            <AdUnit slotType="mobile-header" />
          </div>
          
          <p className="md:hidden" style={{ fontSize: '12px', color: '#9ca3af', maxWidth: '600px', margin: '0 auto', lineHeight: '1.5', marginBottom: '8px' }}>
            Send Large Files Free
          </p>
          <p className="md:hidden" style={{ fontSize: '12px', color: '#9ca3af', maxWidth: '600px', margin: '0 auto', lineHeight: '1.5' }}>
            Unlimited File Size • 100GB Free Bandwidth • Premium Available
          </p>
        </div>

        {/* Main Upload Area */}
        <div className="max-w-6xl mx-auto">
          {!uploadResult && !uploading && (
            <DropZone onFileSelect={handleSelectDirect} />
          )}

          {uploading && !uploadResult && (
            <div style={{ 
              backgroundColor: '#242938', 
              border: '1px solid #374151',
              borderRadius: '2px',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0', marginBottom: '16px' }}>
                {uploadProgress >= 100 && processingProgress < 100 
                  ? `Processing files... (${processedFiles}/${totalFiles})`
                  : totalFiles === 1 
                  ? 'Uploading your file...' 
                  : `Uploading ${totalFiles} files... (${currentFileIndex + 1}/${totalFiles})`
                }
              </h3>
              <ProgressBar 
                progress={uploadProgress >= 100 ? processingProgress : uploadProgress}
                files={files}
                currentFileIndex={currentFileIndex}
                currentFileName={currentFileName}
                totalFiles={totalFiles}
                onCancel={cancelUpload}
                uploadSpeed={uploadSpeed}
                bytesUploaded={bytesUploaded}
                totalBytes={totalBytes}
                destination={currentFolderId || "Secure Storage"}
                processingProgress={processingProgress}
                processedFiles={processedFiles}
              />
            </div>
          )}

          {uploadResult && (
            <ShareLink 
              shareLink={uploadResult.shareLink}
              filename={uploadResult.filename}
              fileCount={uploadResult.fileCount}
              totalSize={uploadResult.totalSize}
              uploadStatus={uploadResult.status}
              onNewUpload={resetUpload}
            />
          )}

          {/* Expiry modal removed; handled by DropZone's inline dropdown */}

          {error && (
            <div style={{ 
              backgroundColor: '#2d1b1b', 
              border: '1px solid #3e2222',
              borderRadius: '2px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg style={{ width: '16px', height: '16px', color: '#f87171' }} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div style={{ marginLeft: '12px' }}>
                  <h3 style={{ fontSize: '12px', fontWeight: '500', color: '#f87171' }}>
                    Upload Failed
                  </h3>
                  <div style={{ marginTop: '8px', fontSize: '11px', color: '#f87171' }}>
                    {error}
                  </div>
                  <div style={{ marginTop: '16px' }}>
                    <button
                      onClick={resetUpload}
                      style={{
                        backgroundColor: '#3e2222',
                        border: '1px solid #4e2a2a',
                        color: '#f87171',
                        padding: '6px 12px',
                        borderRadius: '2px',
                        fontSize: '11px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#4e2a2a'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#3e2222'}
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Google Ads Unit */}
        <div className="max-w-6xl mx-auto mt-8">
          <AdUnit slotType="header" />
        </div>

        {/* Debug Panel - Development Only */}
        {config.features.debugPanel && (
        <div className="max-w-6xl mx-auto mt-8">
          <div style={{ 
            backgroundColor: '#242938', 
            border: '1px solid #374151',
            borderRadius: '2px',
            padding: '16px'
          }}>
            <h3 style={{ fontSize: '12px', fontWeight: '500', color: '#e2e8f0', marginBottom: '12px' }}>
              🔧 Debug Panel (User ID Management)
            </h3>
            <div style={{ marginBottom: '12px' }}>
              <span style={{ fontSize: '10px', color: '#9ca3af' }}>Current User ID: </span>
              <code style={{ 
                fontSize: '10px', 
                color: '#1877f2', 
                backgroundColor: '#1e293b', 
                padding: '2px 4px', 
                borderRadius: '2px' 
              }}>
                {utils.getCurrentUserId()}
              </code>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  console.log('Current User ID:', utils.getCurrentUserId());
                  alert('Check console for current user ID');
                }}
                style={{
                  backgroundColor: '#4b5563',
                  color: '#e2e8f0',
                  border: '1px solid #6b7280',
                  borderRadius: '2px',
                  padding: '4px 8px',
                  fontSize: '10px',
                  cursor: 'pointer'
                }}
              >
                Log Current ID
              </button>
              <button
                onClick={() => {
                  utils.clearUserId();
                  window.location.reload();
                }}
                style={{
                  backgroundColor: '#dc2626',
                  color: '#fff',
                  border: '1px solid #dc2626',
                  borderRadius: '2px',
                  padding: '4px 8px',
                  fontSize: '10px',
                  cursor: 'pointer'
                }}
              >
                Clear & Reload
              </button>
              <button
                onClick={() => {
                  const userId = prompt('Enter user ID (check database for recent uploads):');
                  if (userId) {
                    utils.setUserId(userId);
                    window.location.reload();
                  }
                }}
                style={{
                  backgroundColor: '#059669',
                  color: '#fff',
                  border: '1px solid #059669',
                  borderRadius: '2px',
                  padding: '4px 8px',
                  fontSize: '10px',
                  cursor: 'pointer'
                }}
              >
                Set Specific ID
              </button>
              <button
                onClick={async () => {
                  console.log('🚀 Testing upload speed...');
                  const result = await utils.testUploadSpeed();
                  alert(`Backend Latency: ${result.latency}ms\nStatus: ${result.status}\n${result.latency > 1000 ? 'High latency detected - uploads may be slow' : 'Good latency for fast uploads!'}`);
                }}
                style={{
                  backgroundColor: '#7c3aed',
                  color: '#fff',
                  border: '1px solid #7c3aed',
                  borderRadius: '2px',
                  padding: '4px 8px',
                  fontSize: '10px',
                  cursor: 'pointer'
                }}
              >
                Test Speed
              </button>
            </div>
            <div style={{ marginTop: '8px', fontSize: '9px', color: '#6b7280', lineHeight: '1.3' }}>
            </div>
          </div>
        </div>
        )}

        {/* Features Section */}
        <div className="max-w-4xl mx-auto mt-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* No Signup */}
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '20px' }}>
              <div style={{ color: '#1877f2', marginBottom: '12px' }}>
                <ShieldIcon />
              </div>
              <h3 style={{ fontSize: '13px', fontWeight: '500', color: '#e2e8f0', marginBottom: '8px' }}>
                No Signup Required
              </h3>
              <p style={{ fontSize: '11px', color: '#9ca3af', lineHeight: '1.4' }}>
                Upload and share files instantly without registration, login, or giving personal details. Perfect for quick, hassle-free file transfers.
              </p>
            </div>

            {/* Fast Upload and Download */}
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '20px' }}>
              <div style={{ color: '#10b981', marginBottom: '12px' }}>
                <LightningIcon />
              </div>
              <h3 style={{ fontSize: '13px', fontWeight: '500', color: '#e2e8f0', marginBottom: '8px' }}>
                Fast Uploads and Downloads
              </h3>
              <p style={{ fontSize: '11px', color: '#9ca3af', lineHeight: '1.4' }}>
                Enjoy lightning-fast uploads and downloads through our global CDN network, ensuring reliable and smooth file sharing anywhere in the world.
              </p>
            </div>

            {/* Large Files */}
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '20px' }}>
              <div style={{ color: '#f59e0b', marginBottom: '12px' }}>
                <FileIcon />
              </div>
              <h3 style={{ fontSize: '13px', fontWeight: '500', color: '#e2e8f0', marginBottom: '8px' }}>
                Share Large Files Online
              </h3>
              <p style={{ fontSize: '11px', color: '#9ca3af', lineHeight: '1.4' }}>
                Easily send documents, images, 4k videos, and files of any size. Ideal for work, school, or personal use when email just isn't enough.
              </p>
            </div>

            {/* Secure */}
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '20px' }}>
              <div style={{ color: '#8b5cf6', marginBottom: '12px' }}>
                <LockIcon />
              </div>
              <h3 style={{ fontSize: '13px', fontWeight: '500', color: '#e2e8f0', marginBottom: '8px' }}>
                Secure and Unlimited File Transfers
              </h3>
              <p style={{ fontSize: '11px', color: '#9ca3af', lineHeight: '1.4' }}>
                Share files safely and effortlessly with encryption and automatic link expiration. Files are stored securely in our cloud storage with configurable retention policies.
              </p>
            </div>

          </div>
        </div>

        {/* Navigation */}
        <div className="text-center mt-12">
          <Link 
            to="/uploads"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: '#9ca3af',
              fontSize: '12px',
              textDecoration: 'none',
              padding: '8px 16px',
              border: '1px solid #374151',
              borderRadius: '2px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.color = '#e2e8f0';
              e.target.style.borderColor = '#6b7280';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = '#9ca3af';
              e.target.style.borderColor = '#374151';
            }}
          >
            <FileIcon />
            <span style={{ marginLeft: '8px' }}>View My Files</span>
          </Link>
        </div>

        

        {/* Footer Links */}
        <div className="text-center mt-8 pb-8">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '24px', 
            flexWrap: 'wrap',
            marginBottom: '12px'
          }}>
            <Link 
              to="/privacy-policy"
              style={{
                color: '#6b7280',
                fontSize: '12px',
                textDecoration: 'none',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.color = '#9ca3af'}
              onMouseLeave={(e) => e.target.style.color = '#6b7280'}
            >
              Privacy Policy
            </Link>
            <Link 
              to="/dmca-policy"
              style={{
                color: '#6b7280',
                fontSize: '12px',
                textDecoration: 'none',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.color = '#9ca3af'}
              onMouseLeave={(e) => e.target.style.color = '#6b7280'}
            >
              DMCA Policy
            </Link>
            <Link 
              to="/terms-of-service"
              style={{
                color: '#6b7280',
                fontSize: '12px',
                textDecoration: 'none',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.color = '#9ca3af'}
              onMouseLeave={(e) => e.target.style.color = '#6b7280'}
            >
              Terms of Service
            </Link>
            <Link 
              to="/blog"
              style={{
                color: '#6b7280',
                fontSize: '12px',
                textDecoration: 'none',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.color = '#9ca3af'}
              onMouseLeave={(e) => e.target.style.color = '#6b7280'}
            >
              Blog
            </Link>
          <Link 
            to="/contact"
            style={{
              color: '#6b7280',
              fontSize: '12px',
              textDecoration: 'none',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#9ca3af'}
            onMouseLeave={(e) => e.target.style.color = '#6b7280'}
          >
            Contact
            </Link>
          </div>
          <p style={{ color: '#4b5563', fontSize: '11px', margin: '0' }}>
            Cloud Storage & File Transfer • No Signup Required
          </p>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mt-16" style={{ color: '#e2e8f0' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>Frequently Asked Questions (FAQ)</h2>

          {/* Getting Started */}
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginTop: '16px', marginBottom: '8px' }}>Getting Started</h3>
          <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '16px', marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', lineHeight: 1.7 }}>
              <strong>1. Is LargeTransfer.net really free to use?</strong><br/>
              Yes, absolutely. Our service is 100% free. You can enjoy unlimited uploads and downloads with no hidden costs or premium tiers.
            </p>
          </div>
          <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '16px', marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', lineHeight: 1.7 }}>
              <strong>2. Do I need to create an account or sign up?</strong><br/>
              No. We believe in speed and simplicity. You can start transferring files instantly without any registration or providing personal information.
            </p>
          </div>
          <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '16px', marginBottom: '12px' }}>
            <div style={{ margin: 0, fontSize: '12px', lineHeight: 1.7 }}>
              <p style={{ margin: 0 }}><strong>3. How do I send a file?</strong></p>
              <ol style={{ margin: '8px 0 0 16px' }}>
                <li>Choose or Drag & Drop your file(s) onto our homepage. Select your file expiry.</li>
                <li>Wait for the upload to complete.</li>
                <li>Copy the unique, shareable link and send it to anyone, anywhere.</li>
              </ol>
            </div>
          </div>
          <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '16px', marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', lineHeight: 1.7 }}>
              <strong>4. How do I transfer files from my PC to my mobile (or vice versa)?</strong><br/>
              Simply upload the file from one device and get the shareable link. Then, open that same link in the web browser of your other device to download it. No apps needed!
            </p>
          </div>

          {/* Features & Limits */}
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginTop: '16px', marginBottom: '8px' }}>Features & Limits</h3>
          <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '16px', marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', lineHeight: 1.7 }}>
              <strong>5. What is the maximum file size I can upload?</strong><br/>
              You can upload files of <strong>any size</strong> - there are no file size limits! We provide unlimited cloud storage and file transfer capabilities.
            </p>
          </div>
          <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '16px', marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', lineHeight: 1.7 }}>
              <strong>6. How many times can I upload or download files?</strong><br/>
              We offer <strong>unlimited uploads and unlimited downloads</strong>. There are no caps on the number of transfers you can perform.
            </p>
          </div>
          <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '16px', marginBottom: '12px' }}>
            <div style={{ margin: 0, fontSize: '12px', lineHeight: 1.7 }}>
              <p style={{ margin: 0 }}><strong>7. How long will my files be available for download?</strong></p>
              <ul style={{ margin: '8px 0 0 16px' }}>
                <li>Free users: Files expire based on size (smaller files last longer)</li>
                <li>Premium users: Files stay until subscription ends (no expiry)</li>
              </ul>
              <p style={{ margin: '8px 0 0 0' }}>Files are stored securely in our cloud storage with automatic cleanup after expiry.</p>
            </div>
          </div>
          <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '16px', marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', lineHeight: 1.7 }}>
              <strong>8. Can I send multiple files at once?</strong><br/>
              Yes. You can select multiple files or drop a folder onto the upload area. We will bundle them together in a single, convenient download link.
            </p>
          </div>

          {/* Security & Privacy */}
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginTop: '16px', marginBottom: '8px' }}>Security & Privacy</h3>
          <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '16px', marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', lineHeight: 1.7 }}>
              <strong>9. Are my files secure?</strong><br/>
              Yes. We take security seriously. All files are transferred over a secured connection. Transferred data is stored on secured servers.
            </p>
          </div>
          <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '16px', marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', lineHeight: 1.7 }}>
              <strong>10. What happens to my files after they expire?</strong><br/>
              For your privacy, once a transfer link expires, all associated files are <strong>permanently deleted</strong> from our servers. They cannot be recovered.
            </p>
          </div>
          <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '2px', padding: '16px', marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', lineHeight: 1.7 }}>
              <strong>11. Can I manage or delete my files after uploading them?</strong><br/>
              Yes. After your upload is complete, you will receive a special management link along with your shareable link. Use this management link to view transfer details or to <strong>delete your files at any time</strong> before their natural expiration.
            </p>
          </div>
          <p style={{ fontSize: '12px', color: '#9ca3af' }}>Still have questions? Please reach out via the Contact page.</p>
        </div>
      </div>
    </div>
  );
}

export default HomePage; 
